import { useState, useEffect, useMemo, useRef, memo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ShieldCheck,
} from "lucide-react";
import axios from "axios";
import heartLoading from "../../assests/heartLoading.gif";
import { Alert, Box, Tooltip, Typography } from "@mui/material";
import "./ActionTable.css";
import { RotateCcw, Filter } from "lucide-react";
import Popup from "../../component/Layout/Domain/Popup"
import AppliedFilters from "../../component/common/AppliedFilters";
import ReusableFilterDrawer from "../../component/common/ReusableFilterDrawer";
import CustomMultiSelect from "../../component/Layout/DataContract/Viewkpi/CustomMultiSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const INITIAL_SIZE = 80;
const MORE_SIZE = 40;

const STATUS_MAP = {
  Pending: { color: "text-[#bb1ccc]", bg: "bg-[#EEF2FF]", Icon: Clock },
  "In Review": { color: "text-blue-500", bg: "bg-blue-50", Icon: AlertCircle },
  Approved: {
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    Icon: CheckCircle,
  },
  Rejected: { color: "text-rose-500", bg: "bg-rose-50", Icon: XCircle },
  Overdue: { color: "text-rose-500", bg: "bg-rose-50", Icon: AlertCircle },
  Completed: {
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    Icon: CheckCircle,
  },
  Granted: {
    color: "text-green-600",
    bg: "bg-green-50",
    Icon: ShieldCheck,
  },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.Pending;
  const Icon = s.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[12px] font-semibold ${s.color} ${s.bg}`}
    >
      <Icon size={11} strokeWidth={2.5} /> {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const SortIcon = ({ col, sortColumn, sortOrder }) => {
  if (sortColumn === col) {
    return sortOrder === "asc" ? (
      <ArrowUp size={12} />
    ) : (
      <ArrowDown size={12} />
    );
  }
  return <ArrowUp size={12} className="opacity-30" />;
};

const SearchInput = memo(
  ({ column, table, value, onSearch, debounceRef, onFetch, type = "text" }) => {
    const inputRef = useRef(null);
    const handleDateChange = useCallback(
      (date) => {
        const formatted = date ? date.toISOString().split("T")[0] : "";
        onSearch(table, column, formatted);
        onFetch({ searchOverride: { column, value: formatted } });
      },
      [column, table, onSearch, onFetch]
    );

    const handleSearchChange = useCallback(
      (e) => {
        const newValue = e.target.value;
        onSearch(table, column, newValue);

        if (debounceRef.current[table]) {
          clearTimeout(debounceRef.current[table]);
        }

        debounceRef.current[table] = setTimeout(() => {
          onFetch({ searchOverride: { column, value: newValue } });
        }, 500);
      },
      [column, table, onSearch, debounceRef, onFetch]
    );

    if (type === "date") {
  const parsedValue = value ? new Date(value) : null;

  return (
    <div className="date-search-wrapper" style={{ width: "100%" }}>
      <DatePicker
        selected={parsedValue}
        onChange={handleDateChange}
        dateFormat="MM/dd/yyyy"
        placeholderText="Search"
        isClearable
        showMonthDropdown
        showYearDropdown
        scrollableYearDropdown
        wrapperClassName="date-search-wrapper"
        className="border border-slate-200 rounded-lg px-3 py-2.5 text-xs w-full"
      />
    </div>
  );
}

    return (
      <input
        ref={inputRef}
        type="text"
        className="border border-slate-200 rounded-lg px-3 py-2.5 text-xs w-full"
        placeholder="Search"
        value={value || ""}
        onChange={handleSearchChange}
      />
    );
  }
);

SearchInput.displayName = "SearchInput";


const formatReaderGroup = (readerGroup) => {
  if (!readerGroup) return "N/A";
  return readerGroup.split("|").map((item) => item.trim());
};

const AccessDashboard = ({ isAccessManagementOpen }) => {
const InfoTooltip = () => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex">
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-pointer ml-1.5 text-gray-400"
      >
        &#9432;
      </span>
      {show && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 w-56 z-50 leading-relaxed shadow-lg">
          "Approved" combines two stages: asset owner approval (Status: Approved) and platform provisioning (Status: Granted).<br/> Check the table below for the latest status.
        </div>
      )}
    </div>
  );
};
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("subtab") || "access";
  const setActiveTab = (subtab) => {
    setSearchParams((prev) => {
      const existing = Object.fromEntries(prev.entries());
      return { ...existing, subtab };
    });
  };

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [readerGroupPopup, setReaderGroupPopup] = useState(null);

  // Separate filters for each tab
  const [accessLocalFilters, setAccessLocalFilters] = useState({
    parentDomain: [],
    domain: [],
    subDomain: [],
    product: [],
    category: [],
    startDate: null,
    endDate: null,
  });

  const [actionLocalFilters, setActionLocalFilters] = useState({
    parentDomain: [],
    domain: [],
    subDomain: [],
    product: [],
    category: [],
    startDate: null,
    endDate: null,
  });
  const [accessHasMore, setAccessHasMore] = useState(false);
  const [accessIsFetchingMore, setAccessIsFetchingMore] = useState(false);
  const accessLoadMorePageRef = useRef(Math.ceil(INITIAL_SIZE / MORE_SIZE) + 1);
  const accessLoadedCountRef = useRef(0);
  const accessSentinelRef = useRef(null);

  const [actionHasMore, setActionHasMore] = useState(false);
  const [actionIsFetchingMore, setActionIsFetchingMore] = useState(false);
  const actionLoadMorePageRef = useRef(Math.ceil(INITIAL_SIZE / MORE_SIZE) + 1);
  const actionLoadedCountRef = useRef(0);
  const actionSentinelRef = useRef(null);

  const [accessFilters, setAccessFilters] = useState(null);
  const [actionFilters, setActionFilters] = useState(null);

  const [data, setData] = useState([]);
  const [accessData, setAccessData] = useState([]);
  const [showActionTab, setShowActionTab] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [summary, setSummary] = useState({
    totalRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0,
    pendingRequests: 0,
    totalCount: 0,
  });
  const [accessSummary, setAccessSummary] = useState({
    totalRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0,
    pendingRequests: 0,
    totalCount: 0,
  });

  const userEmail = localStorage.getItem("userId");
  const tabs = showActionTab ? ["access", "action"] : ["access"];
  const [showFilters, setShowFilters] = useState(false);
  const [pendingAccessFetch, setPendingAccessFetch] = useState(false);
  const [pendingActionFetch, setPendingActionFetch] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const debounceTimersRef = useRef({
    access: null,
    action: null,
  });

  const [accessSearch, setAccessSearch] = useState({
    requestedBy: "",
    approver: "",
    assetName: "",
    readerGroup: "",
    status: "",
    date: "",
  });

  const [actionSearch, setActionSearch] = useState({
    productName: "",
    assetName: "",
    requestedBy: "",
    approver: "",
    dueDate: "",
    status: "",
  });
  const stats = [
    {
      label: "Total Requests",
      value:
        activeTab === "access"
          ? accessSummary?.totalRequests
          : summary?.totalRequests,
      color: "#bb1ccc",
    },
    {
      label: "Approved Requests",
      value:
        activeTab === "access"
          ? accessSummary?.approvedRequests
          : summary?.approvedRequests,
      color: "#14B8A6",
    },
    {
      label: "Pending Requests",
      value:
        activeTab === "access"
          ? accessSummary?.pendingRequests
          : summary?.pendingRequests,
      color: "#FACC15",
    },
    {
      label: "Rejected Requests",
      value:
        activeTab === "access"
          ? accessSummary?.rejectedRequests
          : summary?.rejectedRequests,
      color: "#FB7185",
    },
  ];

  const totalRequests = stats[0]?.value || 0;

  const handleSort = (col) => {
    let order = "asc";
    if (sortColumn === col) order = sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(col);
    setSortOrder(order);
    fetchMyActionItems({ sortBy: col, sortOrderParam: order });
  };

  const handleAccessSort = (col) => {
    let order = "asc";
    if (sortColumn === col) order = sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(col);
    setSortOrder(order);
    fetchAccessRequest({ sortBy: col, sortOrderParam: order });
  };

  const accessCols = [
    { key: "productName", label: "Product Name" },
    { key: "assetName", label: "Asset Name" },
    { key: "usageDescription", label: "Usage Description" },
    { key: "requestedBy", label: "Requested By" },
    { key: "approver", label: "Approver" },
    { key: "readerGroup", label: "Reader Group" },
    { key: "status", label: "Status", width: "100px" },
    { key: "date", label: "Request Date", width: "100x", type: "date" },
  ];

  const actionCols = [
    { key: "productName", label: "Product Name" },
    { key: "assetName", label: "Asset Name" },
    { key: "requestedBy", label: "Requested By" },
    { key: "dueDate", label: "Due Date", type: "date" },
    { key: "status", label: "Status" },
    { key: "selected", label: "Select", width: "16px" },
    // { key: "actions", label: "Actions" },
  ];

  const fetchMyActionItems = useCallback(async ({ sortBy, sortOrderParam, searchOverride } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userEmail) params.append("user_email", userEmail);
      if (actionLocalFilters?.parentDomain?.length) params.append("parent_domain", actionLocalFilters.parentDomain.join(","));
      if (actionLocalFilters?.domain?.length) params.append("domain", actionLocalFilters.domain.join(","));
      if (actionLocalFilters?.subDomain?.length) params.append("sub_domain", actionLocalFilters.subDomain.join(","));
      if (actionLocalFilters?.product?.length) params.append("product", actionLocalFilters.product.join(","));
      if (actionLocalFilters?.category?.length) params.append("access_category", actionLocalFilters.category.join(","));
      if (actionLocalFilters?.startDate) params.append("start_date", actionLocalFilters.startDate);
      if (actionLocalFilters?.endDate) params.append("end_date", actionLocalFilters.endDate);

      const effectiveSortBy = sortBy ?? sortColumn;
      const effectiveSortOrder = sortOrderParam ?? sortOrder;
      if (effectiveSortBy) {
        params.append("sort_by", effectiveSortBy);
        params.append("sort_order", effectiveSortOrder);
      }

      const searchSource = searchOverride ? { [searchOverride.column]: searchOverride.value } : actionSearch;
      Object.entries(searchSource).forEach(([col, value]) => {
        if (value && value.trim()) {
          params.append("search_by", col);
          params.append("search_value", value);
        }
      });

      params.append("page", 1);
      params.append("page_size", INITIAL_SIZE);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-subscription-requests?${params.toString()}`
      );

      if (response.data?.status === 200 || response.data?.status === 201) {
        const loaded = response?.data?.data || [];
        const serverTotal = response?.data?.total_count || 0;
        setData(loaded);
        actionLoadedCountRef.current = loaded.length;
        setActionHasMore(serverTotal > loaded.length);
        actionLoadMorePageRef.current = Math.ceil(INITIAL_SIZE / MORE_SIZE) + 1;
        setSummary({
          totalRequests: response?.data?.summary?.total_requests || 0,
          approvedRequests: response?.data?.summary?.approved_requests || 0,
          rejectedRequests: response?.data?.summary?.rejected_requests || 0,
          pendingRequests: response?.data?.summary?.pending_requests || 0,
          totalCount: serverTotal,
        });
        setActionFilters(response.data.filters || null);
      }
    } catch (error) {
      console.error("Error fetching action items:", error);
    } finally {
      setLoading(false);
    }
  }, [actionLocalFilters, sortColumn, sortOrder, actionSearch, userEmail]);

  const loadMoreActionItems = useCallback(async () => {
    if (actionIsFetchingMore || !actionHasMore) return;
    setActionIsFetchingMore(true);
    try {
      const params = new URLSearchParams();
      if (userEmail) params.append("user_email", userEmail);
      if (actionLocalFilters?.parentDomain?.length) params.append("parent_domain", actionLocalFilters.parentDomain.join(","));
      if (actionLocalFilters?.domain?.length) params.append("domain", actionLocalFilters.domain.join(","));
      if (actionLocalFilters?.subDomain?.length) params.append("sub_domain", actionLocalFilters.subDomain.join(","));
      if (actionLocalFilters?.product?.length) params.append("product", actionLocalFilters.product.join(","));
      if (actionLocalFilters?.category?.length) params.append("access_category", actionLocalFilters.category.join(","));
      if (actionLocalFilters?.startDate) params.append("start_date", actionLocalFilters.startDate);
      if (actionLocalFilters?.endDate) params.append("end_date", actionLocalFilters.endDate);
      if (sortColumn) {
        params.append("sort_by", sortColumn);
        params.append("sort_order", sortOrder);
      }
      Object.entries(actionSearch).forEach(([col, value]) => {
        if (value && value.trim()) {
          params.append("search_by", col);
          params.append("search_value", value);
        }
      });
      params.append("page", actionLoadMorePageRef.current);
      params.append("page_size", MORE_SIZE);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-subscription-requests?${params.toString()}`
      );

      if (response.data?.status === 200 || response.data?.status === 201) {
        const newData = response?.data?.data || [];
        const serverTotal = response?.data?.total_count || 0;
        setData((prev) => [...prev, ...newData]);
        actionLoadedCountRef.current += newData.length;
        setActionHasMore(actionLoadedCountRef.current < serverTotal);
        setSummary((prev) => ({ ...prev, totalCount: serverTotal }));
        actionLoadMorePageRef.current += 1;
      }
    } catch (error) {
      console.error("Failed to load more action items:", error);
    } finally {
      setActionIsFetchingMore(false);
    }
  }, [actionIsFetchingMore, actionHasMore, actionLocalFilters, sortColumn, sortOrder, actionSearch, userEmail]);
  const fetchAccessRequest = useCallback(async ({ sortBy, sortOrderParam, searchOverride } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userEmail) params.append("user_email", userEmail);
      if (accessLocalFilters?.parentDomain?.length) params.append("parent_domain", accessLocalFilters.parentDomain.join(","));
      if (accessLocalFilters?.domain?.length) params.append("domain", accessLocalFilters.domain.join(","));
      if (accessLocalFilters?.subDomain?.length) params.append("sub_domain", accessLocalFilters.subDomain.join(","));
      if (accessLocalFilters?.product?.length) params.append("product", accessLocalFilters.product.join(","));
      if (accessLocalFilters?.category?.length) params.append("access_category", accessLocalFilters.category.join(","));
      if (accessLocalFilters?.startDate) params.append("start_date", accessLocalFilters.startDate);
      if (accessLocalFilters?.endDate) params.append("end_date", accessLocalFilters.endDate);

      const effectiveSortBy = sortBy ?? sortColumn;
      const effectiveSortOrder = sortOrderParam ?? sortOrder;
      if (effectiveSortBy) {
        params.append("sort_by", effectiveSortBy);
        params.append("sort_order", effectiveSortOrder);
      }

      const searchSource = searchOverride ? { [searchOverride.column]: searchOverride.value } : accessSearch;
      Object.entries(searchSource).forEach(([col, value]) => {
        if (value && value.trim()) {
          params.append("search_by", col);
          params.append("search_value", value);
        }
      });

      params.append("page", 1);
      params.append("page_size", INITIAL_SIZE);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-subscription-requests/all?${params.toString()}`
      );

      if (response.data?.status === 200 || response.data?.status === 201) {
        const loaded = response?.data?.data || [];
        const serverTotal = response?.data?.total_count || 0;
        setAccessData(loaded);
        accessLoadedCountRef.current = loaded.length;
        setAccessHasMore(serverTotal > loaded.length);
        accessLoadMorePageRef.current = Math.ceil(INITIAL_SIZE / MORE_SIZE) + 1;
        setAccessSummary({
          totalRequests: response?.data?.summary?.total_requests || 0,
          approvedRequests: response?.data?.summary?.approved_requests || 0,
          rejectedRequests: response?.data?.summary?.rejected_requests || 0,
          pendingRequests: response?.data?.summary?.pending_requests || 0,
          totalCount: serverTotal,
        });
        setAccessFilters(response.data.filters || null);
        setShowActionTab(response?.data?.showActionItems);
      }
    } catch (error) {
      console.error("Error fetching access requests:", error);
    } finally {
      setLoading(false);
    }
  }, [accessLocalFilters, sortColumn, sortOrder, accessSearch, userEmail]);

  const loadMoreAccessRequests = useCallback(async () => {
    if (accessIsFetchingMore || !accessHasMore) return;
    setAccessIsFetchingMore(true);
    try {
      const params = new URLSearchParams();
      if (userEmail) params.append("user_email", userEmail);
      if (accessLocalFilters?.parentDomain?.length) params.append("parent_domain", accessLocalFilters.parentDomain.join(","));
      if (accessLocalFilters?.domain?.length) params.append("domain", accessLocalFilters.domain.join(","));
      if (accessLocalFilters?.subDomain?.length) params.append("sub_domain", accessLocalFilters.subDomain.join(","));
      if (accessLocalFilters?.product?.length) params.append("product", accessLocalFilters.product.join(","));
      if (accessLocalFilters?.category?.length) params.append("access_category", accessLocalFilters.category.join(","));
      if (accessLocalFilters?.startDate) params.append("start_date", accessLocalFilters.startDate);
      if (accessLocalFilters?.endDate) params.append("end_date", accessLocalFilters.endDate);
      if (sortColumn) {
        params.append("sort_by", sortColumn);
        params.append("sort_order", sortOrder);
      }
      Object.entries(accessSearch).forEach(([col, value]) => {
        if (value && value.trim()) {
          params.append("search_by", col);
          params.append("search_value", value);
        }
      });
      params.append("page", accessLoadMorePageRef.current);
      params.append("page_size", MORE_SIZE);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-subscription-requests/all?${params.toString()}`
      );

      if (response.data?.status === 200 || response.data?.status === 201) {
        const newData = response?.data?.data || [];
        const serverTotal = response?.data?.total_count || 0;
        setAccessData((prev) => [...prev, ...newData]);
        accessLoadedCountRef.current += newData.length;
        setAccessHasMore(accessLoadedCountRef.current < serverTotal);
        setAccessSummary((prev) => ({ ...prev, totalCount: serverTotal }));
        accessLoadMorePageRef.current += 1;
      }
    } catch (error) {
      console.error("Failed to load more access requests:", error);
    } finally {
      setAccessIsFetchingMore(false);
    }
  }, [accessIsFetchingMore, accessHasMore, accessLocalFilters, sortColumn, sortOrder, accessSearch, userEmail]);
  const submitRequest = async (type) => {
    const payload = {
      request_ids: selectedIds,
      action: type?.charAt(0).toUpperCase() + type?.slice(1),
      user_email: userEmail,
    };
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/subscription-status-update`,
        payload
      );

      if (response.data?.status === 200 || response.data?.status === 201) {
        setToast({
          open: true,
          message: `Requests ${type?.charAt(0).toUpperCase() + type?.slice(1)
            } successfully`,
          severity: "success",
        });
        fetchMyActionItems();
      }
    } catch (error) {
      console.error("Error fetching incidents:", error);
      setToast({
        open: true,
        message: `Action failed. Please try again later.`,
        severity: "error",
      });
    } finally {
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((i) => i !== id);
      else return [...prev, id];
    });
  };

  const resetAll = () => {
    const empty = { parentDomain: [], domain: [], subDomain: [], product: [], category: [], startDate: null, endDate: null };
    setAccessLocalFilters(empty);
    setActionLocalFilters(empty);

    setSortColumn("");
    setSortOrder("asc");
    setAccessSearch({
      requestedBy: "",
      approver: "",
      assetName: "",
      readerGroup: "",
      status: "",
      date: "",
    });
    setActionSearch({
      productName: "",
      assetName: "",
      requestedBy: "",
      approver: "",
      dueDate: "",
      status: "",
    });

    fetchMyActionItems();
    fetchAccessRequest();
  };

  const handleAccessSearchChange = useCallback((table, column, value) => {
    setAccessSearch({ [column]: value });
  }, []);

  const handleActionSearchChange = useCallback((table, column, value) => {
    setActionSearch({ [column]: value });
  }, []);

  useEffect(() => {
    fetchAccessRequest();
    fetchMyActionItems();
  }, []);

  useEffect(() => {
    if (pendingAccessFetch) {
      fetchAccessRequest();
      setPendingAccessFetch(false);
    }
  }, [pendingAccessFetch, fetchAccessRequest]);

  useEffect(() => {
    if (pendingActionFetch) {
      fetchMyActionItems();
      setPendingActionFetch(false);
    }
  }, [pendingActionFetch, fetchMyActionItems]);

  useEffect(() => {
    const el = accessSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && accessHasMore && !accessIsFetchingMore) loadMoreAccessRequests();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [accessHasMore, accessIsFetchingMore, loadMoreAccessRequests]);

  useEffect(() => {
    const el = actionSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && actionHasMore && !actionIsFetchingMore) loadMoreActionItems();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [actionHasMore, actionIsFetchingMore, loadMoreActionItems]);

  const currentFiltersData = activeTab === "access" ? accessFilters : actionFilters;

  const filterSections = useMemo(() => {
    const pdList = currentFiltersData?.parentDomains || currentFiltersData?.parent_domains || [];
    const allDomsFlat = pdList.flatMap((pd) =>
      (pd.domains || []).map((d) => ({ ...d, parentDomainName: pd.parent_domain }))
    );
    const allSubsFlat = pdList.flatMap((pd) =>
      (pd.domains || []).flatMap((d) =>
        (d.sub_domains || []).map((sd) => ({ ...sd, parentDomainName: pd.parent_domain, domainName: d.domain }))
      )
    );
    const allProdsFlat = pdList.flatMap((pd) =>
      (pd.domains || []).flatMap((d) =>
        (d.sub_domains || []).flatMap((sd) =>
          (sd.products || []).map((p) => ({
            name: p,
            parentDomainName: pd.parent_domain,
            domainName: d.domain,
            subDomainName: sd.sub_domain,
          }))
        )
      )
    );
    const categories = currentFiltersData?.accessCategory || [];

    const fields = [
      {
        type: "custom", key: "parentDomain", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          const opts = pdList.map((pd) => ({ value: pd.parent_domain, label: pd.parent_domain }));
          const currentValue = opts.filter((o) => (localFilters.parentDomain || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Parent Domain"
              options={opts}
              value={currentValue}
              placeholder="All Parent Domains"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, parentDomain: vals, domain: [], subDomain: [], product: [] }));
              }}
            />
          );
        },
      },
      {
        type: "custom", key: "domain", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          let filtered = allDomsFlat;
          if (localFilters.parentDomain?.length) filtered = filtered.filter((d) => localFilters.parentDomain.includes(d.parentDomainName));
          const unique = Array.from(new Map(filtered.map((d) => [d.domain, d])).values());
          const opts = unique.map((d) => ({ value: d.domain, label: d.domain }));
          const currentValue = opts.filter((o) => (localFilters.domain || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Domain"
              options={opts}
              value={currentValue}
              placeholder="All Domains"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, domain: vals, subDomain: [], product: [] }));
              }}
            />
          );
        },
      },
      {
        type: "custom", key: "subDomain", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          let filtered = allSubsFlat;
          if (localFilters.parentDomain?.length) filtered = filtered.filter((sd) => localFilters.parentDomain.includes(sd.parentDomainName));
          if (localFilters.domain?.length) filtered = filtered.filter((sd) => localFilters.domain.includes(sd.domainName));
          const unique = Array.from(new Map(filtered.map((sd) => [sd.sub_domain, sd])).values());
          const opts = unique.map((sd) => ({ value: sd.sub_domain, label: sd.sub_domain }));
          const currentValue = opts.filter((o) => (localFilters.subDomain || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Sub Domain"
              options={opts}
              value={currentValue}
              placeholder="All Sub Domains"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, subDomain: vals, product: [] }));
              }}
            />
          );
        },
      },
      {
        type: "custom", key: "product", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          let filtered = allProdsFlat;
          if (localFilters.parentDomain?.length) filtered = filtered.filter((p) => localFilters.parentDomain.includes(p.parentDomainName));
          if (localFilters.domain?.length) filtered = filtered.filter((p) => localFilters.domain.includes(p.domainName));
          if (localFilters.subDomain?.length) filtered = filtered.filter((p) => localFilters.subDomain.includes(p.subDomainName));
          const unique = Array.from(new Map(filtered.map((p) => [p.name, p])).values());
          const opts = unique.map((p) => ({ value: p.name, label: p.name }));
          const currentValue = opts.filter((o) => (localFilters.product || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Product"
              options={opts}
              value={currentValue}
              placeholder="All Products"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, product: vals }));
              }}
            />
          );
        },
      },
      ...(categories.length > 0 ? [{
        type: "custom", key: "category", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          const opts = categories.map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));
          const currentValue = opts.filter((o) => (localFilters.category || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Access Category"
              options={opts}
              value={currentValue}
              placeholder="All Categories"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, category: vals }));
              }}
            />
          );
        },
      }] : []),
      { type: "date", key: "startDate", label: "Start Date", defaultValue: null },
      { type: "date", key: "endDate", label: "End Date", defaultValue: null },
    ];

    return [{ fields }];
  }, [currentFiltersData, activeTab]);

  const handleDrawerApply = (drawerFilters) => {
    if (activeTab === "access") {
      setAccessLocalFilters(drawerFilters);
      setPendingAccessFetch(true);
    } else {
      setActionLocalFilters(drawerFilters);
      setPendingActionFetch(true);
    }
  };

  if (loading && data?.length === 0) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
          <span className="text-gray-700 text-[20px]">
            Hang tight, fetching data....
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen incident-wrapper">
      <div className="flex justify-between items-center">
        <div className="flex flex-col pb-4">
          <h1 className="text-[24px] font-bold">Access Management</h1>
          <div className="text-[14px]  text-gray-600">
            Manage access requests and track your pending action items
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-4 px-4 gap-4 mb-3">
        {stats.map((s) => (
          <>
            <Box
              key={s.label}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                padding: 3,
                height: "120px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                border: `1px solid  ${s.color}`,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "0 12px 30px rgba(0, 0, 0, 0.2) !important",
                  transform: "translateY(-6px) !important",
                  border: "1px solid #EDE9FE !important",
                  backgroundColor: "#f8f9ff",
                },
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: s.color,
                    fontSize: "1.1rem",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {s.label !== "Total Requests" && totalRequests > 0
                    ? `${s.label} (${((s.value / totalRequests) * 100).toFixed(0)}%)`
                    : s.label
                  }
                  {s.label === "Approved Requests" && (
                    <InfoTooltip text="Access has been provided. For granted status, keep checking requests in the dashboard." />
                  )}
                </Typography>
                <Box
                  sx={{
                    height: "3px",
                    width: "40px",
                    backgroundColor: s.color,
                    borderRadius: "2px",
                    mt: 0.5,
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: s.color,
                  fontSize: "2rem",
                }}
              >
                {s.value}
              </Typography>
            </Box>
          </>
        ))}
      </div>

      {/* Tabs */}

      {(() => {
        const currentFilters = activeTab === "access" ? accessLocalFilters : actionLocalFilters;
        const filterCount = [
          ...(currentFilters.parentDomain || []),
          ...(currentFilters.domain || []),
          ...(currentFilters.subDomain || []),
          ...(currentFilters.product || []),
          ...(currentFilters.category || []),
          currentFilters.startDate,
          currentFilters.endDate,
        ].filter(Boolean).length;
        return (
          <div className="flex justify-end mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap bg-[#bb1ccc] text-white border-[#bb1ccc] hover:bg-[#4f52d1]"
              >
                <Filter className="h-4 w-4" />
                Filters
                {filterCount > 0 && (
                  <span className="bg-white text-[#bb1ccc] text-xs rounded-full px-1.5 py-0.5 leading-none font-bold">
                    {filterCount}
                  </span>
                )}
              </button>
              <button
                onClick={resetAll}
                className="px-4 py-2.5 text-sm font-medium text-white bg-[#bb1ccc] border rounded-lg hover:bg-[#4f52d1] flex items-center gap-2 h-[45px] whitespace-nowrap"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            </div>
          </div>
        );
      })()}

      {(() => {
        const currentFilters = activeTab === "access" ? accessLocalFilters : actionLocalFilters;
        const chips = [];
        (currentFilters.parentDomain || []).forEach((v) => chips.push({ label: "Parent Domain", value: v }));
        (currentFilters.domain || []).forEach((v) => chips.push({ label: "Domain", value: v }));
        (currentFilters.subDomain || []).forEach((v) => chips.push({ label: "Sub Domain", value: v }));
        (currentFilters.product || []).forEach((v) => chips.push({ label: "Product", value: v }));
        (currentFilters.category || []).forEach((v) => chips.push({ label: "Category", value: v }));
        if (currentFilters.startDate) chips.push({ label: "Start Date", value: currentFilters.startDate });
        if (currentFilters.endDate) chips.push({ label: "End Date", value: currentFilters.endDate });
        return chips.length > 0 ? (
          <div className="mb-2">
            <AppliedFilters chips={chips} />
          </div>
        ) : null;
      })()}

      <div className="bg-[#eee] rounded-2xl shadow-sm border border-slate-100 px-4 py-2">
        <div className="flex justify-between items-center mb-4 border-b border-slate-100">
          {/* LEFT SIDE → Tabs */}
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setSortColumn("");
                }}
                className={`px-4 py-2 text-base font-semibold rounded-t-lg transition-colors ${activeTab === tab
                  ? "text-[#bb1ccc] border-b-2 border-[#bb1ccc] -mb-px bg-white"
                  : "text-slate-400 hover:text-slate-600"
                  }`}
              >
                {tab === "access" ? "Access Requests" : "My Action Items"} (
                {tab === "access"
                  ? accessSummary?.totalCount || accessData?.length || 0
                  : summary?.totalCount || data?.length || 0}
                )
              </button>
            ))}
          </div>

          {/* RIGHT SIDE → Approve / Reject Buttons */}
          {selectedIds?.length > 0 && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => submitRequest("approved")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-3 py-2 flex items-center gap-1"
              >
                <CheckCircle className="h-5" />
                <span>Approve Selected</span>
              </button>

              <button
                onClick={() => submitRequest("rejected")}
                className="bg-rose-500 hover:bg-rose-600 text-white rounded-md px-3 py-2 flex items-center gap-1"
              >
                <XCircle className="h-5" />
                <span>Reject Selected</span>
              </button>
            </div>
          )}
        </div>

        {activeTab === "access" && (
          <>
            {accessData?.length > 0 && (
              <div className="px-4 py-2 text-[13px] text-gray-500">
                {accessData.length} of {accessSummary?.totalCount || 0} results loaded
              </div>
            )}
            <div style={{ overflow: "auto", maxHeight: "calc(100vh - 350px)" }}>
              <table className="w-full min-w-[600px]">
                <thead className="text-[#fff] font-normal w-full [&_th]:sticky [&_th]:z-[2] [&_th]:!bg-[#1a0020] [&_tr:first-child_th]:top-0 [&_tr:last-child_th]:top-[37px]">
                  <tr>
                    {accessCols.map(({ key, label, width, type }) => (
                      <th
                        key={key}
                        onClick={() => handleAccessSort(key)}
                        style={{ width: width || "auto" }}
                        className={`px-4 pb-[6px] pr-[2px] text-[14px] pt-[10px] text-left whitespace-nowrap w-[14%] cursor-pointer select-none font-normal ${width ? "w-[100px]" : ""
                          }`}
                      >
                        <span className="flex items-center gap-1">
                          {label}
                          <SortIcon
                            col={key}
                            sortColumn={sortColumn}
                            sortOrder={sortOrder}
                          />
                        </span>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {accessCols.map(({ key, label, width, type }) => (
                      <th
                        key={key}
                        style={{ width: width || "auto" }}
                        className="px-4 pt-0 pb-[10px] font-normal"
                      >
                        <SearchInput
                          column={key}
                          table="access"
                          value={accessSearch[key]}
                          onSearch={handleAccessSearchChange}
                          debounceRef={debounceTimersRef}
                          onFetch={fetchAccessRequest}
                          type={type || "text"}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accessData?.length > 0 ? (
                    accessData?.map((r) => (
                      <tr
                        key={r.request_id}
                        className="border-b border-slate-50 hover:bg-[#CCFBF1] transition-colors"
                      >
                        <td className="px-3 py-4 mono text-[12px]   max-w-[220px] truncate">
                          <Tooltip title={r?.product_name}>
                            {r?.product_name}
                          </Tooltip>
                        </td>
                        <td className="px-3 py-4 text-[12px]  max-w-[220px] truncate">
                          <Tooltip title={r?.data_asset_name}>
                            {r?.data_asset_name}
                          </Tooltip>
                        </td>
                        <td className="px-3 py-4 text-[12px]  max-w-[220px] truncate">
                          <Tooltip title={r?.usage_description}>
                            {r?.usage_description}
                          </Tooltip>
                        </td>

                        <td className="px-3 py-4 text-[12px]  max-w-[160px] truncate">
                          <Tooltip title={r?.requested_by}>
                            {r?.requested_by}
                          </Tooltip>
                        </td>

                        <td className="px-3 py-4 text-[12px]  max-w-[160px] truncate">
                          <Tooltip title={r?.data_asset_owner}>
                            {r?.data_asset_owner}
                          </Tooltip>
                        </td>

                        <td className="px-3 py-4 text-[12px]">
                          {(() => {
                            const groups = formatReaderGroup(r.reader_group);
                            const visible = groups.slice(0, 2);
                            const extra = groups.length - visible.length;
                            return (
                              <div onClick={() => setReaderGroupPopup(groups)} className="space-y-1 max-w-[220px]">
                                {visible.map((item, idx) => (
                                  <div key={idx} className="truncate">{item}</div>
                                ))}
                                {extra > 0 && (
                                  <button
                                    onClick={() => setReaderGroupPopup(groups)}
                                    className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-[#bb1ccc] hover:bg-[#EDE9FE] border border-gray-200 transition-colors"
                                  >
                                    +{extra} more
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>

                        <td className="px-3 py-4 text-[12px] w-[100px]">
                          <StatusBadge status={r.status} />
                        </td>

                        <td
                          className={`px-3 py-4 text-[12px] font-medium w-[100px] ${r.overdue ? "text-rose-500" : ""
                            }`}
                        >
                          {r.requested_date
                            ? (() => {
                              const [day, month, year] =
                                r.requested_date.split("/");
                              const formattedDate = new Date(
                                year,
                                month - 1,
                                day
                              );

                              return formattedDate.toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "2-digit",
                                  year: "numeric",
                                }
                              );
                            })()
                            : "N/A"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={accessCols.length}
                        className="py-2 text-center"
                      >
                        <p className="text-slate-500">No Data found.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div ref={accessSentinelRef} style={{ height: 1 }} />
            {accessIsFetchingMore && (
              <div className="flex items-center justify-center py-4 gap-2">
                <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
                <span className="text-gray-500 text-sm">Loading more...</span>
              </div>
            )}
            {!accessHasMore && accessData?.length > 0 && (
              <div className="text-center py-3 text-gray-400 text-sm">
                All {accessSummary?.totalCount || 0} results loaded
              </div>
            )}
          </>
        )}

        {activeTab === "action" && showActionTab && (
          <>
            {data?.length > 0 && (
              <div className="px-4 py-2 text-[13px] text-gray-500">
                {data.length} of {summary?.totalCount || 0} results loaded
              </div>
            )}
            <div style={{ overflow: "auto", maxHeight: "calc(100vh - 350px)" }}>
              <table className="w-full">
                <thead className="text-[#404040] font-normal [&_th]:sticky [&_th]:z-[2] [&_th]:bg-[#F0FDFA] [&_tr:first-child_th]:top-0 [&_tr:last-child_th]:top-[37px]">
                  <tr>
                    {actionCols.map(({ key, label, width }) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        style={{ width: width || "auto" }}
                        className={`px-4 pb-[6px] pr-[2px] text-[14px] pt-[10px] text-left whitespace-nowrap w-[14%] cursor-pointer select-none font-normal  ${key === "select" ? "w-[60px]" : ""
                          }`}
                      >
                        <span className="flex items-center gap-1">
                          {label}
                          {label !== "Select" && (
                            <SortIcon
                              col={key}
                              sortColumn={sortColumn}
                              sortOrder={sortOrder}
                            />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {actionCols.map(({ key, label, width, type }) => (
                      <th
                        key={key}
                        style={{ width: width || "auto" }}
                        className="px-4 pt-0 pb-[10px] text-[14px] font-normal"
                      >
                        {label !== "Select" && (
                          <SearchInput
                            column={key}
                            table="action"
                            value={actionSearch[key]}
                            onSearch={handleActionSearchChange}
                            debounceRef={debounceTimersRef}
                            onFetch={fetchMyActionItems}
                            type={type || "text"}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 ? (
                    data?.map((item) => (
                      <tr
                        key={item?.request_id}
                        className="border-b border-slate-50 hover:bg-[#CCFBF1] transition-colors"
                      >
                        <td className="px-3 py-4 mono text-[12px]   align-top pt-5  max-w-[180px] truncate">
                          {item?.product_name}
                        </td>
                        <td className="px-3 py-4">
                          <p className="text-[12px]   max-w-[220px] truncate">
                            {item.data_asset_name}
                          </p>
                        </td>

                        <td className="px-3 py-4 text-[12px]  max-w-[160px] truncate ">
                          {item?.requested_by}
                        </td>
                        <td
                          className={`px-3 py-4 text-[12px] font-medium ${item.overdue ? "text-rose-500" : ""
                            }`}
                        >
                          {item?.deadline_date
                            ? (() => {
                              const [day, month, year] =
                                item.deadline_date.split("/");
                              const formattedDate = new Date(
                                year,
                                month - 1,
                                day
                              );

                              return formattedDate.toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "2-digit",
                                  year: "numeric",
                                }
                              );
                            })()
                            : "N/A"}
                        </td>
                        <td className="px-3 py-4 text-[12px]">
                          <StatusBadge status={item?.status} />
                        </td>

                        <td className="px-3 pl-4 py-4 mono w-[60px]">
                          {item?.status === "Approved" ||
                            item?.status === "Rejected" ? (
                            <p className="text-[12px] text-green-500">
                              Action Taken
                            </p>
                          ) : (
                            <input
                              type="checkbox"
                              className="mr-2 rounded border-slate-300 text-teal-500 focus:ring-teal-300 text-[12px]"
                              onChange={() => handleSelect(item?.request_id)}
                            />
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={accessCols.length}
                        className="py-2 text-center"
                      >
                        <p className="text-slate-500">No Data found.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div ref={actionSentinelRef} style={{ height: 1 }} />
            {actionIsFetchingMore && (
              <div className="flex items-center justify-center py-4 gap-2">
                <img src={heartLoading} alt="Loading more..."style={{ width : 200}} />
                <span className="text-gray-500 text-sm">Loading more...</span>
              </div>
            )}
            {!actionHasMore && data?.length > 0 && (
              <div className="text-center py-3 text-gray-400 text-sm">
                All {summary?.totalCount || 0} results loaded
              </div>
            )}
          </>
        )}
      </div>
      {toast.open && (
        <Alert
          severity={toast.severity}
          onClose={() => setToast({ ...toast, open: false })}
          className="fixed top-20 right-4 z-50"
        >
          {toast.message}
        </Alert>
      )}
      <ReusableFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        sections={filterSections}
        values={activeTab === "access" ? accessLocalFilters : actionLocalFilters}
        onApply={handleDrawerApply}
        onReset={() => {
          const empty = { parentDomain: [], domain: [], subDomain: [], product: [], category: [], startDate: null, endDate: null };
          if (activeTab === "access") setAccessLocalFilters(empty);
          else setActionLocalFilters(empty);
        }}
        flat
        subtitle="Filter by domain, product & date"
      />

      {readerGroupPopup && (
        <Popup
          title="Reader Groups"
          items={readerGroupPopup}
          type="text"
          onClose={() => setReaderGroupPopup(null)}
        />
      )}
    </div>
  );
};

export default AccessDashboard;
